import React, { useState, useEffect } from "react";
import "./Occupancy.css";
import Sidebar from "./sidebar";
import WeeklyOccupancyTable from "./WeeklyOccupancy";

const Occupancy = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roomData, setRoomData] = useState({});
  const [occupiedCount, setOccupiedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deskAssignees, setDeskAssignees] = useState({});
  const [deskAvgOccupancy, setDeskAvgOccupancy] = useState({});
  
  // Replace this with your actual GitHub Pages URL
  const IFRAME_URL = "https://pwwpdev.github.io/innoport.html";

  const toggleSidebar = () => {
      setSidebarOpen(!sidebarOpen);
  };

  const fetchHistoricalData = async () => {
    try {
      // Calculate start date (Monday of current week)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const startDate = new Date(today);
      
      // Get Monday of current week
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate.setDate(today.getDate() - daysToSubtract);
      startDate.setHours(0, 0, 0, 0);
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        return date.toISOString().split("T")[0];
      };
      
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = formatDate(today);
      
      const url = `https://njs-01.optimuslab.space/api/desks/occupancy/history?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Process data to calculate average occupancy by desk
      const avgOccupancyMap = {};
      
      data.forEach(desk => {
        if (desk.daily_occupancy && desk.daily_occupancy.length > 0) {
          const dailyPercentages = desk.daily_occupancy
            .filter(day => {
              const dayDate = new Date(day.date);
              const dayOfWeek = dayDate.getDay();
              return dayOfWeek > 0; // Filter out Sunday (0)
            })
            .map(day => parseFloat(day.percentage) || 0);
          
          const totalPercentage = dailyPercentages.reduce(
            (sum, percentage) => sum + percentage, 0
          );
          
          avgOccupancyMap[desk.desk_id] = dailyPercentages.length > 0
            ? totalPercentage / dailyPercentages.length
            : 0;
        } else {
          avgOccupancyMap[desk.desk_id] = 0;
        }
      });
      
      setDeskAvgOccupancy(avgOccupancyMap);
    } catch (err) {
      console.error("Error fetching historical data:", err);
    }
  };

  const fetchDeskInfo = async () => {
    try {
      const response = await fetch("https://njs-01.optimuslab.space/api/desks");
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform into a map with desk_id as key and assignee_name as value
      const assigneeMap = {};
      data.forEach(desk => {
        assigneeMap[desk.desk_id] = desk.assignee_name || "Unassigned";
      });
      
      setDeskAssignees(assigneeMap);
    } catch (err) {
      console.error("Error fetching desk info:", err);
    }
  };
  
  // Fetch live occupancy data from API
  const fetchOccupancyData = async () => {
      try {
          setLoading(true);
          const response = await fetch("https://njs-01.optimuslab.space/api/desks/occupancy/live");
          const data = await response.json();
          
          // Transform API data into a map with desk_id as key and occupancy status as value
          const transformedData = {};
          data.forEach(desk => {
              transformedData[desk.desk_id] = desk.occupied === 1;
          });
          
          setRoomData(transformedData);
          
          // Calculate occupied count
          const newCount = data.filter(desk => desk.occupied === 1).length;
          setOccupiedCount(newCount);
          
          setLoading(false);
      } catch (error) {
          console.error("Error fetching occupancy data:", error);
          setLoading(false);
      }
  };

  // Initial data fetch and set up interval for updates
  useEffect(() => {
      fetchOccupancyData();
      fetchHistoricalData();
      fetchDeskInfo(); 
      
      // Refresh data every 30 seconds (or whatever interval makes sense)
      const interval = setInterval(() => {
          fetchOccupancyData();
          fetchHistoricalData();
          fetchDeskInfo(); 
      }, 30000);
      
      return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gray-50 flex flex-col min-h-screen">
    
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

            {/* Header */}
            <div className="bg-white p-6 shadow flex items-center relative">
            {/* Hamburger Menu */}
            <button 
            onClick={toggleSidebar}
            className="text-gray-600 hover:text-gray-900 focus:outline-none z-10"
            >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            </button>

            {/* Centered Logo */}
            <div className="absolute left-0 right-0 mx-auto flex justify-center">
            <img 
            src="/download.png" 
            alt="CUHK INNOPORT" 
            className="h-12" 
            />
            </div>
            </div>
            <h1 className="font-semibold py-6 px-10 text-[28px]">Live Occupancy</h1>
        
        <div className="flex flex-col items-center lg:flex-row lg:justify-between lg:items-start mt-2 w-full">
            {/* Statistics Panel */}
            <div className="mb-6 px-8">
                <div className="bg-white pb-6 pl-6 pr-6 pt-5 w-[352px] sm:w-[604px] md:w-[702px] lg:w-[340px] xl:w-[430px] 2xl:w-[466px] rounded-xl border border-[#E2E2E4] shadow-[0_1px_2px*0*#dedede]">
                {/* Title and Count */}
                <div className="flex justify-between items-start mb-2">
                    <h2 className="text-sm sm:text-sm md:text-md lg:text-lg xl:text-lg 2xl:text-lg font-semibold text-gray-800">Room 2A</h2>
                    <p className="text-2xl sm:text-xl md:text-2xl lg:text-3xl xl:text-3xl 2xl:text-3xl font-extrabold text-blue-800">
                    {loading ? "..." : occupiedCount}
                    </p>
                </div>
            
            {/* Progress Bar */}
            <div className="relative w-full h-3 bg-gray-300 rounded-lg overflow-hidden">
                <div 
                className="h-full bg-blue-800" 
                style={{ 
                    width: loading ? "0%" : `${(occupiedCount / Object.keys(roomData).length) * 100}%`,
                    transition: 'width 0.3s ease'
                }}
                ></div>
            </div>
            
            {/* Occupancy Percentage Text */}
            <div className="flex justify-between">
            <p className="text-sm text-gray-600 mt-2">
                {loading ? "Loading..." : `${((occupiedCount / Object.keys(roomData).length) * 100).toFixed(0)}%`}
            </p>
            <p className="text-xs mt-2.5 text-gray-500">
                Maximum Capacity: {loading ? "..." : Object.keys(roomData).length}
            </p>
            </div>

          
        </div>
    </div>
      
      {/* 3D Viewer iframe */}
      <div className="relative flex lg:mr-8 lg:w-full md:w-[700px] w-[350px]  bg-white rounded-xl border border-[#E2E2E4] shadow-[0_1px_2px*0*#dedede] overflow-hidden mx-4 lg:mx-0">
        <div className="lg:w-full md:w-full w-full">
          <iframe 
            src={IFRAME_URL}
            width="100%" 
           
            frameBorder="0"
            title="Desk Occupancy Viewer"
            className="lg:w-full min-h-[180px] md:min-h-[350px] lg:min-h-[290px] xl:min-h-[484px] 2xl:min-h-[490px] "
            style={{ border: "none" }}
          />
          <div className="m-2 p-2 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Legend</h3>
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#FCC4BA] rounded mr-2"></div>
                  <span className="text-gray-600">Occupied</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#B8EAC5] rounded mr-2"></div>
                  <span className="text-gray-600">Vacant</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded mr-2"></div>
                  <span className="text-gray-600">Unavailable</span>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>

    <WeeklyOccupancyTable />

            {/* Footer */}
            <div className="mt-auto pl-4 py-4 text-center text-sm text-gray-500">
            <div className='flex flex-row justify-center gap-2'>
                <p className="mb-1">Powered by Power Workplace</p> 
                <img src='/image.png' width={24} height={14} /> 
            </div>
            <p>Terms of Service</p>
            </div>
      
    </div>
  );

};

export default Occupancy;